import React, { useState, useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import {
  MDBContainer,
  MDBRow,
  MDBCol,
  MDBIcon,
  MDBBtn,
  MDBCard,
  MDBCardHeader,
  MDBCardBody,
  MDBBadge,
  MDBModal,
  MDBModalHeader,
  MDBModalBody,
  MDBInput,
  MDBModalDialog,
  MDBModalContent,
  MDBModalTitle,
} from 'mdb-react-ui-kit'
import UserProfileService from './../../../shared/services/UserProfile.service'
import { UserContractRequestFactory } from './../../../shared/factories'
import ACMStore from './../AgentContractManager.store'
import UsersCarrierContractService from './../../../shared/services/UsersCarrierContract.service'

import './AvailableContractsList.scss'
import { AdditionalCarrierFilter } from './AdditionalCarrierFilter'
import UserService from '../../../shared/services/User.service'

const RenderCarrierTable = observer(({ isLocked, priority, Carriers, onOpenSiblingModal }) => {
  const [ContractRequest, setContractRequest] = useState(null)
  const renderPill = indicator => {
    switch (indicator.props.children) {
      case '$$$':
        return (
          <MDBBadge pill color='primary'>
            {'Core'}
          </MDBBadge>
        )
      case '$$':
        return (
          <MDBBadge pill color='danger'>
            {'Preferred'}
          </MDBBadge>
        )
      case '$':
        return (
          <MDBBadge pill color='success'>
            {'Additional'}
          </MDBBadge>
        )
      default:
        return
    }
  }

  const renderSelectableRow = (Carrier, idx) => {
    const mustWishlist = !!Carrier?.must_wishlist,
      getIsRequested = () => {
        return ACMStore.ContractRequests.some(CR => {
          if (CR.get('is_expired')) {
            return false
          }
          return parseInt(CR.get('carrier_id')) === parseInt(Carrier.id())
        })
      }
    let addedToWishlist = getIsRequested()

    return (
      <div
        className={
          'selectable-row ' +
          (isCarrierSelected(Carrier) ? 'row-selected ' : '') +
          (mustWishlist ? 'wishlist-row ' : '')
        }
        key={`selectable-cell-carrier-${Carrier.id()}`}
      >
        <div className='selectable-cell cell-name'>
          <h6>{Carrier.get('c_name')}</h6>
          <div className='d-flex align-items-center'>
            <small className='mr-2'>{Carrier.renderPriorityIndicator(UserProfileService.getCurrentUserTypeId())}</small>
            {renderPill(Carrier.renderPriorityIndicator(UserProfileService.getCurrentUserTypeId()))}
            {UserProfileService.isA(['system-admin', 'internal-admin', 'internal-staff'], true) ? (
              <>
                &nbsp;
                <MDBBtn
                  type='button'
                  color='default'
                  onClick={async () => {
                    // Create an 'Override' contract.
                    UserProfileService.instance().then(async User => {
                      const C = await User.contract().create({
                        carrier_id: Carrier.id(),
                        contract_id: 'manual',
                        disposition: 'awaiting',
                      })
                      await C.save()
                      ACMStore.set('contracts', C)
                    })
                  }}
                  className='override-carrier-btn'
                >
                  <MDBIcon icon='plus' /> Force Add
                </MDBBtn>
              </>
            ) : (
              <></>
            )}
          </div>
        </div>

        <div className='selectable-cell cell-descrip'>
          <div>{Carrier.get('c_description')}</div>
        </div>

        <div className='selectable-cell cell-action'>
          <MDBBtn
            type='button'
            color='default'
            disabled={addedToWishlist}
            className={'toggle-carrier-btn ' + (addedToWishlist ? 'selected' : '')}
            onClick={async () => {
              if (mustWishlist) {
                UserProfileService.instance().then(async User => {
                  try {
                    const ContractRequest = await User.contractRequest().create({
                      carrier_id: Carrier.id(),
                    })
                    await ContractRequest.save()
                    ACMStore.add('ContractRequest', ContractRequest)
                    addedToWishlist = getIsRequested()
                    setContractRequest(ContractRequest)
                  } catch (ex) {
                    console.log({ ContractRequestEx: `${ex}` })
                  }
                })
              } else {
                ACMStore.add(
                  'CartItem',
                  await ACMStore.Cart.item(true).create({
                    carrier_id: Carrier.id(),
                    item_status: 'DRAFT',
                  })
                )
              }
            }}
          >
            {mustWishlist ? (
              addedToWishlist ? (
                <>
                  <MDBIcon icon='check' />
                  &nbsp;<span>Contract Requested</span>
                </>
              ) : (
                <>
                  <MDBIcon fas icon='clipboard-list' />
                  &nbsp;<span>Request Contract</span>
                </>
              )
            ) : isCarrierSelected(Carrier) ? (
              <>
                <MDBIcon icon='check' />
                &nbsp;<span>Added to Cart</span>
              </>
            ) : (
              <>
                <MDBIcon icon='cart-plus' />
                &nbsp;<span>Add to Cart</span>
              </>
            )}
          </MDBBtn>
        </div>
      </div>
    )
  }

  const renderSiblingsRow = (Carriers, idx) => {
    const setPriorityIndicator = () => {
      const indicatorArray = Carriers.map(Carrier =>
        Carrier.renderPriorityIndicator(UserProfileService.getCurrentUserTypeId())
      )

      const priorityIndicators = {
        $$$: [],
        $$: [],
        $: [],
      }

      // Group indicators by priority level
      indicatorArray.forEach(item => {
        const priority = item.props.children
        if (priority in priorityIndicators) {
          priorityIndicators[priority].push(item)
        }
      })

      // Find the first available indicator based on priority level
      for (const priority of ['$$$', '$$', '$']) {
        if (priorityIndicators[priority].length > 0) {
          return priorityIndicators[priority][0]
        }
      }

      // Return a default indicator if none found
      return null
    }

    return (
      <div className={'selectable-sibling-row '} key={`selectable-sibling-cell-carrier-${idx}`}>
        <div className='selectable-cell cell-name'>
          <h6>{Carriers[0].get('c_name')}</h6>
          <div className='d-flex align-items-center'>
            <small className='mr-2'>{setPriorityIndicator()}</small>
            {renderPill(setPriorityIndicator())}
            {UserProfileService.isA(['system-admin', 'internal-admin', 'internal-staff'], true) ? (
              <>
                &nbsp;
                <MDBBtn
                  type='button'
                  color='default'
                  size='sm'
                  onClick={() => onOpenSiblingModal(Carriers)}
                  className='override-carrier-btn'
                >
                  <MDBIcon icon='plus' /> Force Add
                </MDBBtn>
              </>
            ) : (
              <></>
            )}
          </div>
        </div>
        <div className='sibling-rows'>
          {Carriers.map((Carrier, idx) => {
            return (
              <div
                className={'sibling-cell-row ' + (isCarrierSelected(Carrier) ? 'row-selected' : '')}
                key={`sibling-cell-row-${Carrier.id()}-${idx}`}
              >
                <div className='selectable-cell cell-descrip'>
                  <div>{Carrier.get('c_description')}</div>
                </div>

                <div className='selectable-cell cell-action'>
                  <MDBBtn
                    type='button'
                    color='default'
                    className='toggle-carrier-btn'
                    onClick={async () =>
                      ACMStore.add(
                        'CartItem',
                        await ACMStore.Cart.item(true).create({
                          carrier_id: Carrier.id(),
                          item_status: 'DRAFT',
                        })
                      )
                    }
                  >
                    {isCarrierSelected(Carrier) ? (
                      <>
                        <MDBIcon icon='check' />
                        &nbsp;<span>Added to Cart</span>
                      </>
                    ) : (
                      <>
                        <MDBIcon icon='cart-plus' />
                        &nbsp;<span>Add to Cart</span>
                      </>
                    )}
                  </MDBBtn>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  const selectedCarrierIds = ACMStore.CartItems.map(
      CI => CI.get('item_status') !== 'ABANDONED' && CI.get('carrier_id')
    ).filter(n => !isNaN(n)),
    isCarrierSelected = Carrier => ~selectedCarrierIds.indexOf(parseInt(Carrier.id()))

  if (Carriers.length === 0)
    return (
      <div className='selectable-carriers-table empty-list'>
        Wow, you've contracted with all our {priority} Carriers! Great Job!
        <br />
        <strong>There are no more available {priority} carrier contracts.</strong>
      </div>
    )

  const RequestedCarrier = Carriers.find(Carrier => {
    return !Array.isArray(Carrier)
      ? parseInt(Carrier.id()) === parseInt(ContractRequest?.get('carrier_id'))
      : Carrier.find(C => parseInt(C.id()) === parseInt(ContractRequest?.get('carrier_id')))
  })

  return (
    <>
      <div className='selectable-carriers-table'>
        {Carriers.map((Carrier, idx) => {
          if (Array.isArray(Carrier)) return renderSiblingsRow(Carrier, idx)
          return renderSelectableRow(Carrier, idx)
        })}
      </div>
      <MDBModal open={!!ContractRequest} onClose={() => setContractRequest(null)} className='contract-request-modal'>
        <MDBModalDialog>
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>Requested Contract</MDBModalTitle>
              <MDBBtn
                type='button'
                className='btn-close'
                color='none'
                onClick={() => setContractRequest(null)}
              ></MDBBtn>
            </MDBModalHeader>
            <MDBModalBody>
              <p>
                Great! The {RequestedCarrier?.get('c_name')} carrier contract has been requested. Hang tight while we
                work on this for you.
              </p>
              <br />
              <p>Come back here after 3 business days & proceed to complete carrier contracting.</p>
              <br />
              <MDBBtn type='button' block size='sm' color='success' onClick={() => setContractRequest(null)}>
                Close
              </MDBBtn>
            </MDBModalBody>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>
    </>
  )
})

const AvailableContractsList = ({ activeSpecs }) => {
  const [specialization, setSpecialization] = useState('ALL')
  const [uplines, setUplines] = useState([])
  const [divisionLeaderContracts, setDivisionLeaderContracts] = useState([])
  const [divisionLeaderContractsLoaded, setDivisionLeaderContractsLoaded] = useState(false)
  const [showSiblingModal, setShowSiblingModal] = useState(false)
  const [selectedSiblingCarriers, setSelectedSiblingCarriers] = useState([])
  const [currentSiblingGroup, setCurrentSiblingGroup] = useState([])
  const [downlineContractRequests, setDownlineContractRequests] = useState([])
  const [agentContractRequests, setAgentContractRequests] = useState([])

  useEffect(() => {
    getUplines()
    getDivisionLeaderContracts()
    getDownlineContractRequests()
    getAgentContractRequests()
  }, [])

  const getUplines = async () => {
    const upline_agents = await UserService.getUpline(UserProfileService.getUserId())
    setUplines(upline_agents.map(upline => `${upline.id}`))
  }

  const getDivisionLeaderContracts = async () => {
    try {
      const result = await UsersCarrierContractService.getDivisionLeaderCarriers()
      setDivisionLeaderContracts((result?.carrier_ids || []).map(id => parseInt(id)))
      setDivisionLeaderContractsLoaded(true)
    } catch (error) {
      console.error('Error getting division leader contracts:', error)
      setDivisionLeaderContracts([])
      setDivisionLeaderContractsLoaded(true)
    }
  }

  const openSiblingModal = siblingCarriers => {
    setCurrentSiblingGroup(siblingCarriers)
    setSelectedSiblingCarriers([])
    setShowSiblingModal(true)
  }

  const closeSiblingModal = () => {
    setShowSiblingModal(false)
    setCurrentSiblingGroup([])
    setSelectedSiblingCarriers([])
  }

  const handleSiblingSelection = carrierId => {
    // Convert to string for consistent data types (like EmailDistribution pattern)
    const carrierIdStr = String(carrierId)
    setSelectedSiblingCarriers(prev => {
      const selected = [...prev]
      if (selected.includes(carrierIdStr)) {
        // Remove if already selected
        return selected.filter(id => id !== carrierIdStr)
      } else {
        // Add if not selected
        return [...selected, carrierIdStr]
      }
    })
  }

  const handleForceAddSelected = async () => {
    if (selectedSiblingCarriers.length === 0) return

    for (const carrierId of selectedSiblingCarriers) {
      UserProfileService.instance().then(async User => {
        const C = await User.contract().create({
          carrier_id: carrierId,
          contract_id: 'manual',
          disposition: 'awaiting',
        })
        await C.save()
        ACMStore.set('contracts', C)
      })
    }
    closeSiblingModal()
  }

  const selectAllSiblings = () => {
    setSelectedSiblingCarriers(currentSiblingGroup.map(carrier => String(carrier.id())))
  }

  const clearAllSiblings = () => {
    setSelectedSiblingCarriers([])
  }

  const getDownlineContractRequests = async () => {
    try {
      setDownlineContractRequests(
        (await UserContractRequestFactory.search({
          search: { _downline: `${UserProfileService.getUserId()}` },
          pagination: false,
        })) || []
      )
    } catch (ex) {
      setDownlineContractRequests([])
    }
  }

  const getAgentContractRequests = async () => {
    try {
      setAgentContractRequests(
        (await UserContractRequestFactory.search({
          search: {
            user_id: `${UserProfileService.getUserId()}`,
            is_expired: 0,
          },
          pagination: false,
        })) || []
      )
    } catch (ex) {
      setAgentContractRequests([])
    }
  }

  const isCarrierSelectable = Carrier => {
    if (parseInt(Carrier.get('c_active')) !== 1) return false

    if (parseInt(Carrier.get('c_active')) === 1) {
      if (Carrier.get('visibility')?.contracting?.enable) {
        const usertype_id = parseInt(UserProfileService.getCurrentUserTypeId())
        if (Carrier.get('visibility')?.contracting?.commission?.enable) {
          let userDetails = UserProfileService.getUserDetails()
          if (Carrier.get('visibility')?.contracting?.commission?.[userDetails.commission_id]) {
            return false
          }
        }
        if ([225].includes(usertype_id) && !Carrier.get('visibility')?.contracting?.career_agent) return false
        if ([111, 133].includes(usertype_id) && !Carrier.get('visibility')?.contracting?.affiliate_agent) return false
        if ([223, 224].includes(usertype_id) && !Carrier.get('visibility')?.contracting?.associate_agent) return false
        if (
          [91, 92, 149, 37, 35, 38, 90, 93, 130, 226, 227, 228, 219, 36, 222].includes(usertype_id) &&
          !Carrier.get('visibility')?.contracting?.sig_agent
        )
          return false
      } else return false

      if ((Carrier.get('disabled_uplines') || []).includes(UserProfileService.get('u_upline_id'))) return false
    }

    if (Carrier.get('visibility')?.contracting?.upline_ids?.length > 0) {
      const disabled_carrier = Carrier.get('visibility').contracting.upline_ids.some(upline_id =>
        uplines.includes(upline_id)
      )
      if (disabled_carrier) return false
    }

    // remove carriers that ...
    // 	1. are in the current shopping cart.
    // 	2. are in a past 'CLOSED' shopping cart AND disposition != 'unsent' AND disposition != 'expired'.

    // remove carriers in the cart (excluding abandonded).
    // return false if carrier is already in shopping cart.
    if (
      ACMStore.CartItems.filter(
        Item => `${Item.get('carrier_id')}` === `${Carrier.id()}` && Item.get('item_status') !== 'ABANDONED'
      ).length > 0
    )
      return false

    // remove carriers already contracted (excluding expired).
    // return false if carrier is already contracted.
    if (
      ACMStore.Contracts.filter(
        Contract =>
          `${Contract.get('carrier_id')}` === `${Carrier.id()}` &&
          Contract.get('disposition') !== 'expired' &&
          Contract.get('disposition') !== 'unsent'
      ).length > 0
    )
      return false

    return true
  }

  const shouldWishlist = (carrierId, divisionLeaderContracts) => {
    // If no division leader found or no contracts, don't show any carriers
    if (divisionLeaderContracts.length === 0) return true

    // Only show carriers that the division leader has contracted with
    if (!divisionLeaderContracts.includes(carrierId)) return true

    return false
  }

  const loadSelectableCarriers = () => {
    const CarrierGroups = {
        core: [],
        preferred: [],
        additional: [],
      },
      downlineRequestedCarrierIds = (downlineContractRequests || [])?.map(CR => parseInt(CR.get('carrier_id'))),
      agentRequestedCarrierIds = (agentContractRequests || [])?.map(CR => parseInt(CR.get('carrier_id'))),
      filteredCarriers = (ACMStore?.Carriers || []).filter(isCarrierSelectable).map(Carrier => {
        // New logic: Only show carriers IF the agent's nearest upline with division commission level (130)
        // is contracted with the respective carrier (with pwk_signed_at completed)
        // Exclude users with specific roles from this filtering
        // const isAdmin = UserProfileService.isA(
        //   ['internal-admin', 'internal-staff', 'system-admin'],
        //   true
        // )
        // If the agent has a downline agent with an open contract request,
        // then we will NOT force the agent to put this on wishlist/contract request.
        // The logic here is such that if an agent has requested a contract,
        // ANY of the agents leaders can proceed with contracting.

        Carrier.must_wishlist = false

        const isExcluded = UserProfileService.isA(['divisional-leader', 'agency-owner']),
          isInDownlineReqs = ~downlineRequestedCarrierIds.indexOf(parseInt(Carrier.id())),
          isInAgentReqs = ~agentRequestedCarrierIds.indexOf(parseInt(Carrier.id()))

        const carrierId = parseInt(Carrier.id())

        if (divisionLeaderContractsLoaded && !isExcluded && !isInDownlineReqs) {
          Carrier.must_wishlist = shouldWishlist(carrierId, divisionLeaderContracts)
        } else if (divisionLeaderContractsLoaded && !isExcluded && isInAgentReqs) {
          Carrier.must_wishlist = shouldWishlist(carrierId, divisionLeaderContracts)
        }

        return Carrier
      })

    filteredCarriers.forEach(Carrier => {
      const carrierType = Carrier.getPriority(activeSpecs, UserProfileService.get('usertype_id'))

      if (carrierType === 'additional') {
        if (specialization !== 'ALL') {
          if (Carrier.getCoverageCategory().filter(c => c.coverage_category === specialization).length === 0) {
            return
          }
        }
      }
      CarrierGroups[carrierType].push(Carrier)
    })

    return CarrierGroups
  }

  const organizeSiblings = Carriers => {
    // const siblingIds = {additional: []};
    const CarriersById = {},
      LinkedSiblings = {}
    Carriers.forEach(Carrier => (CarriersById[Carrier.id()] = Carrier))

    for (let carrierId in CarriersById) {
      if (CarriersById.hasOwnProperty(carrierId) && CarriersById[carrierId].get('sibling_ids'))
        LinkedSiblings[carrierId] = Array.isArray(CarriersById[carrierId].get('sibling_ids'))
          ? CarriersById[carrierId].get('sibling_ids')
          : JSON.parse(CarriersById[carrierId].get('sibling_ids'))

      if (LinkedSiblings.hasOwnProperty(carrierId) && Array.isArray(LinkedSiblings[carrierId])) {
        LinkedSiblings[carrierId] = LinkedSiblings[carrierId]
          .map(siblingId => {
            let Sibling
            if (CarriersById.hasOwnProperty(siblingId) && CarriersById[siblingId]) {
              Sibling = CarriersById[siblingId]
              delete CarriersById[siblingId]
              return Sibling
            }
            return Sibling
          })
          .filter(Carrier => Carrier)

        if (LinkedSiblings[carrierId].length === 0) {
          delete LinkedSiblings[carrierId]
        } else {
          LinkedSiblings[carrierId].unshift(CarriersById[carrierId])
          delete CarriersById[carrierId]
        }
      }
    }

    return Object.values(CarriersById)
      .concat(Object.values(LinkedSiblings))
      .sort((CarrierA, CarrierB) => {
        CarrierA = Array.isArray(CarrierA) ? CarrierA[0] : CarrierA
        CarrierB = Array.isArray(CarrierB) ? CarrierB[0] : CarrierB
        return `${CarrierA.get('c_name')}`.localeCompare(`${CarrierB.get('c_name')}`, 'en')
      })
  }

  const organizePrioritySiblings = ({ core, preferred, additional }) => {
    return {
      core: organizeSiblings(core),
      preferred: organizeSiblings(preferred),
      additional: organizeSiblings(additional),
    }
  }

  const renderDropDown = () => {
    const CarrierGroups = organizePrioritySiblings(loadSelectableCarriers())
    const isLoading = false

    const renderTable = priority => {
      let isLocked = false
      if (priority === 'preferred') isLocked = (CarrierGroups.core.length || 0) > 0
      if (priority === 'additional')
        isLocked = parseInt(CarrierGroups.core.length || 0) + parseInt(CarrierGroups.preferred.length || 0) > 0

      const getPriorityIndicator = priority => {
        switch (priority && `${priority}`.trim().toLowerCase()) {
          case 'core':
            return '$$$'
          case 'preferred':
            return '$$'
          case 'additional':
            return '$'
          default:
            return ''
        }
      }

      const getPriorityTableName = priority => {
        switch (priority && `${priority}`.trim().toLowerCase()) {
          case 'core':
            return 'USABG Core Carriers'
          case 'preferred':
            return 'USABG Preferred Carriers'
          case 'additional':
            return 'Additional Carriers'
          default:
            return ''
        }
      }

      const getPriorityTableDescrip = priority => {
        switch (priority && `${priority}`.trim().toLowerCase()) {
          case 'core':
            return 'Core carriers provide agents with the best support, service and commissions. All agents are required to have core carriers in their portfolio.'
          case 'preferred':
            return "Preferred carriers provide additional coverage for your client's protection. We highly recommend all agents have these products to better serve their clients."
          case 'additional':
            return ''
          default:
            return ''
        }
      }

      return (
        <MDBCard className='mb-4 carrier-table-card'>
          <MDBCardHeader
            className={
              // CarrierStore.getIsLoading('fetch.carriers') ||
              // CarrierStore.getIsLoading('fetch.agent-carriers') ||
              // CarrierStore.getIsLoading('update.agent-specs') ||
              // CarrierStore.getIsLoading('fetch.agent-specs')
              isLoading ? 'is-loading' : ''
            }
          >
            <MDBRow>
              <MDBCol size='12'>
                <div>
                  <strong>
                    <span className='priority-level-indicator'>{getPriorityIndicator(priority)}</span>
                  </strong>
                  &nbsp;{getPriorityTableName(priority)}&nbsp;
                  <span className='loader-wheel'>
                    <span>
                      <i className='fa fa-spin fa-spinner' />
                    </span>
                    &nbsp;Updating Carriers ...
                  </span>
                  {priority === 'additional' ? (
                    <AdditionalCarrierFilter onChange={e => setSpecialization(e)} value={specialization} />
                  ) : (
                    <></>
                  )}
                </div>
                <small>{getPriorityTableDescrip(priority)}</small>
              </MDBCol>
            </MDBRow>
          </MDBCardHeader>
          <MDBCardBody>
            <RenderCarrierTable
              isLocked={isLocked}
              priority={priority}
              Carriers={CarrierGroups[priority]}
              onOpenSiblingModal={openSiblingModal}
            />
          </MDBCardBody>
        </MDBCard>
      )
    }

    return (
      <div>
        {renderTable('core')}
        {renderTable('preferred')}
        {renderTable('additional')}
      </div>
    )
  }

  const renderSiblingSelectionModal = () => {
    return (
      <MDBModal open={showSiblingModal} onClose={closeSiblingModal}>
        <MDBModalDialog size='lg'>
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>Select Sibling Carriers to Force Add</MDBModalTitle>
              <MDBBtn type='button' className='btn-close' color='none' onClick={closeSiblingModal}></MDBBtn>
            </MDBModalHeader>
            <MDBModalBody>
              <div className='sibling-selection-modal'>
                <p>
                  <strong>{currentSiblingGroup[0]?.get('c_name')}</strong> - Select which sibling carriers to force add:
                </p>
                <div className='mb-3 d-flex' style={{ gap: '0.5rem' }}>
                  <MDBBtn
                    type='button'
                    size='sm'
                    color='secondary'
                    className='select-btn'
                    onClick={selectAllSiblings}
                    disabled={selectedSiblingCarriers.length === currentSiblingGroup.length}
                  >
                    <MDBIcon icon='check-double' /> Select All
                  </MDBBtn>
                  <MDBBtn
                    type='button'
                    size='sm'
                    color='danger'
                    className='clear-btn'
                    onClick={clearAllSiblings}
                    disabled={selectedSiblingCarriers.length === 0}
                  >
                    <MDBIcon icon='times' /> Clear All
                  </MDBBtn>
                </div>
                {currentSiblingGroup.map((Carrier, idx) => {
                  const isSelected = selectedSiblingCarriers.includes(String(Carrier.id()))
                  return (
                    <div
                      key={`modal-carrier-${Carrier.id()}`}
                      className={`mb-3 p-3 border rounded modal-carrier-item ${isSelected ? 'selected' : ''}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSiblingSelection(Carrier.id())}
                    >
                      <div className='d-flex align-items-center'>
                        <div className='form-check mr-3'>
                          <MDBInput
                            type='checkbox'
                            id={`carrier-checkbox-${Carrier.id()}`}
                            checked={isSelected}
                            onChange={() => handleSiblingSelection(Carrier.id())}
                            wrapperClass='mb-0'
                          />
                        </div>
                        <div className='flex-grow-1'>
                          <h6 className='mb-1'>{Carrier.get('c_name')}</h6>
                          <small className='text-muted'>{Carrier.get('c_description')}</small>
                        </div>
                        {isSelected && <MDBIcon icon='check-circle' className='text-success ml-2' />}
                      </div>
                    </div>
                  )
                })}
                <div className='modal-footer-buttons mt-4 d-flex justify-content-end' style={{ gap: '0.5rem' }}>
                  <MDBBtn type='button' color='danger' onClick={closeSiblingModal}>
                    Cancel
                  </MDBBtn>
                  <MDBBtn
                    type='button'
                    color='secondary'
                    onClick={handleForceAddSelected}
                    disabled={selectedSiblingCarriers.length === 0}
                    className='override-carrier-btn'
                  >
                    <MDBIcon icon='plus' /> Force Add Selected ({selectedSiblingCarriers.length})
                  </MDBBtn>
                </div>
              </div>
            </MDBModalBody>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>
    )
  }

  const renderWidget = () => {
    return (
      <MDBContainer className='AvailableContractsListComponent' fluid>
        <MDBRow>
          <MDBCol size='12'>{renderDropDown()}</MDBCol>
        </MDBRow>
        {renderSiblingSelectionModal()}
      </MDBContainer>
    )
  }

  return renderWidget()
}

export default observer(AvailableContractsList)
